import { useState, useEffect } from "react";
import { Search, Gift, Download, Smartphone, Shield, FileText } from "lucide-react";
import api from "@/lib/api";

const CATEGORIES = ["All", "Primary TET", "Upper Primary TET", "C-TET", "Group C", "Group D", "SSC", "Banking", "General"];

export default function FreeEbooksPage() {
  const [books, setBooks] = useState([]);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchBooks(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== "All") params.append("category", category);
      const { data } = await api.get(`/free-ebooks?${params.toString()}`);
      setBooks(data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const filtered = search ? books.filter(b => b.title.toLowerCase().includes(search.toLowerCase())) : books;

  const handleDownload = async (book) => {
    try { await api.post(`/free-ebooks/${book.id}/track-download`); } catch (err) { console.error(err); }
    window.open(book.file_url, "_blank");
  };

  return (
    <div className="page-with-nav bg-white min-h-screen" data-testid="free-ebooks-page">
      {/* Header */}
      <div className="bg-[#059669] px-4 pt-4 pb-5 text-white">
        <div className="flex items-center gap-2 mb-1">
          <Gift size={20} />
          <h1 className="text-xl font-black" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Free E-Books
          </h1>
        </div>
        <p className="text-xs text-white/80">Download free PDF books - No payment required!</p>
        <div className="relative mt-3">
          <Search className="absolute left-3 top-3 text-green-300" size={16} />
          <input
            data-testid="free-ebooks-search"
            type="text"
            placeholder="Search free books..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-green-700 border-b-2 border-green-500 pl-9 pr-4 py-2.5 text-sm text-white placeholder-green-300 focus:outline-none focus:border-white transition-none"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="bg-green-50 px-4 py-3">
        <div className="flex overflow-x-auto gap-2 scrollbar-hide">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`flex-shrink-0 text-xs font-bold px-3 py-2 transition-none ${
                category === cat ? "bg-[#059669] text-white" : "bg-white text-slate-600 border border-slate-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Features */}
      <div className="px-4 py-2 bg-green-50 border-b border-green-100 flex items-center gap-4">
        <div className="flex items-center gap-1.5">
          <Gift size={12} className="text-green-600" />
          <span className="text-[10px] font-bold text-green-700">100% Free</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Download size={12} className="text-green-600" />
          <span className="text-[10px] font-bold text-green-700">Instant Download</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Smartphone size={12} className="text-green-600" />
          <span className="text-[10px] font-bold text-green-700">Printable</span>
        </div>
      </div>

      {/* Books List */}
      <div className="px-4 py-4">
        <p className="text-xs text-slate-500 mb-3">{filtered.length} free books available</p>
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Gift size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-400">No free e-books found</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="free-ebooks-list">
            {filtered.map((book) => (
              <div key={book.id} data-testid={`free-ebook-${book.id}`} className="bg-white border border-slate-100">
                <div className="flex gap-3 p-3">
                  {/* Cover */}
                  <div className="w-24 aspect-[3/4] bg-slate-100 flex-shrink-0 overflow-hidden">
                    {book.image_url ? (
                      <img src={book.image_url} alt={book.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-green-50 flex items-center justify-center">
                        <FileText size={28} className="text-green-600" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 flex flex-col justify-between py-1">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase text-[#059669]">{book.category}</span>
                        <span className="bg-green-100 text-green-700 text-[10px] font-bold px-1.5 py-0.5">FREE</span>
                      </div>
                      <h3 className="text-sm font-bold text-slate-900 mt-0.5 line-clamp-2">{book.title}</h3>
                      {book.author && <p className="text-xs text-slate-500 mt-0.5">by {book.author}</p>}
                      <div className="flex items-center gap-2 mt-1">
                        {book.file_format && (
                          <span className="bg-[#059669]/10 text-[#059669] text-[10px] font-bold px-1.5 py-0.5">{book.file_format}</span>
                        )}
                        {book.pages && <span className="text-[10px] text-slate-400">{book.pages}</span>}
                        {book.file_size && <span className="text-[10px] text-slate-400">{book.file_size}</span>}
                      </div>
                    </div>

                    <div className="mt-2 flex items-center gap-2">
                      <span className="font-black text-lg text-[#059669]">FREE</span>
                      <span className="text-[10px] text-slate-400">{book.downloads || 0} downloads</span>
                    </div>
                  </div>
                </div>

                {book.description && (
                  <p className="px-3 pb-2 text-xs text-slate-500 line-clamp-2">{book.description}</p>
                )}

                {/* Download Button */}
                <div className="px-3 pb-3">
                  {book.file_url ? (
                    <button
                      onClick={() => handleDownload(book)}
                      data-testid={`download-free-${book.id}`}
                      className="w-full bg-[#059669] text-white font-bold py-3.5 text-sm flex items-center justify-center gap-2 transition-none hover:bg-[#047857]"
                    >
                      <Download size={18} /> Download Now - FREE
                    </button>
                  ) : (
                    <div className="w-full bg-slate-200 text-slate-500 font-bold py-3.5 text-sm flex items-center justify-center gap-2">
                      Coming Soon
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
